/**
 * Late-binding i18n keys for the NEW badge + What's New drawer + Feature
 * tour. Kept in a separate module so adding new strings doesn't require
 * editing the giant 2700-line i18n.js file in 10 places.
 *
 * Imported & merged into `translations` at module load via i18n.js's
 * registerLateKeys helper.
 *
 * Languages covered (mirrors LANGUAGES in i18n.js):
 *   en, ar, ur, hi, bn, fr, es, tr, tl (Filipino), ml
 */
export const WHATS_NEW_I18N = {
  en: {
    new_badge_label: 'NEW',
    whats_new_title: "What's New",
    whats_new_subtitle: 'Last 7 days',
    whats_new_caught_up: "You're all caught up",
    whats_new_caught_up_desc: 'Nothing new in the last 7 days. Recently shipped features automatically appear here.',
    whats_new_show_dismissed: 'Show dismissed items',
    whats_new_mark_one: 'Mark as read',
    whats_new_mark_all: 'Mark all as read',
    whats_new_today: 'today',
    whats_new_yesterday: 'yesterday',
    whats_new_days_ago: '{n} days ago',
    whats_new_n_items: '{n} items',
    whats_new_n_item: '1 item',
    whats_new_try_feature: 'Try this feature',
    whats_new_got_it: 'Got it',
    whats_new_close: 'Close',
    cat_ux: 'UX',
    cat_reliability: 'Reliability',
    cat_reconciliation: 'Reconciliation',
    cat_ai: 'AI',
  },
  ar: {
    new_badge_label: 'جديد',
    whats_new_title: 'ما الجديد',
    whats_new_subtitle: 'آخر 7 أيام',
    whats_new_caught_up: 'أنت على اطلاع كامل',
    whats_new_caught_up_desc: 'لا جديد خلال آخر 7 أيام. تظهر الميزات المنشورة حديثاً هنا تلقائياً.',
    whats_new_show_dismissed: 'إظهار العناصر المهملة',
    whats_new_mark_one: 'تعليم كمقروء',
    whats_new_mark_all: 'تعليم الكل كمقروء',
    whats_new_today: 'اليوم',
    whats_new_yesterday: 'أمس',
    whats_new_days_ago: 'قبل {n} أيام',
    whats_new_n_items: '{n} عنصر',
    whats_new_n_item: 'عنصر واحد',
    whats_new_try_feature: 'جرّب هذه الميزة',
    whats_new_got_it: 'فهمت',
    whats_new_close: 'إغلاق',
    cat_ux: 'تجربة',
    cat_reliability: 'موثوقية',
    cat_reconciliation: 'تسوية',
    cat_ai: 'ذكاء',
  },
  ur: {
    new_badge_label: 'نیا',
    whats_new_title: 'کیا نیا ہے',
    whats_new_subtitle: 'گزشتہ 7 دن',
    whats_new_caught_up: 'آپ سب کچھ دیکھ چکے ہیں',
    whats_new_caught_up_desc: 'پچھلے 7 دنوں میں کچھ نیا نہیں۔ نئی شائع شدہ خصوصیات یہاں خود بخود ظاہر ہوں گی۔',
    whats_new_show_dismissed: 'مسترد کی گئی اشیاء دکھائیں',
    whats_new_mark_one: 'پڑھا ہوا نشان زد کریں',
    whats_new_mark_all: 'سب کو پڑھا ہوا نشان زد کریں',
    whats_new_today: 'آج',
    whats_new_yesterday: 'کل',
    whats_new_days_ago: '{n} دن پہلے',
    whats_new_n_items: '{n} اشیاء',
    whats_new_n_item: '1 شے',
    whats_new_try_feature: 'یہ خصوصیت آزمائیں',
    whats_new_got_it: 'سمجھ گیا',
    whats_new_close: 'بند کریں',
    cat_ux: 'تجربہ',
    cat_reliability: 'قابل اعتماد',
    cat_reconciliation: 'مفاہمت',
    cat_ai: 'AI',
  },
  hi: {
    new_badge_label: 'नया',
    whats_new_title: 'क्या नया है',
    whats_new_subtitle: 'पिछले 7 दिन',
    whats_new_caught_up: 'आप पूरी तरह अपडेट हैं',
    whats_new_caught_up_desc: 'पिछले 7 दिनों में कुछ नया नहीं। हाल ही में लॉन्च की गई सुविधाएँ यहाँ स्वतः दिखेंगी।',
    whats_new_show_dismissed: 'छोड़े गए आइटम दिखाएँ',
    whats_new_mark_one: 'पढ़ा हुआ चिह्नित करें',
    whats_new_mark_all: 'सभी को पढ़ा हुआ चिह्नित करें',
    whats_new_today: 'आज',
    whats_new_yesterday: 'कल',
    whats_new_days_ago: '{n} दिन पहले',
    whats_new_n_items: '{n} आइटम',
    whats_new_n_item: '1 आइटम',
    whats_new_try_feature: 'यह सुविधा आज़माएँ',
    whats_new_got_it: 'समझ गया',
    whats_new_close: 'बंद करें',
    cat_ux: 'UX',
    cat_reliability: 'विश्वसनीयता',
    cat_reconciliation: 'मिलान',
    cat_ai: 'AI',
  },
  bn: {
    new_badge_label: 'নতুন',
    whats_new_title: 'নতুন কী',
    whats_new_subtitle: 'গত ৭ দিন',
    whats_new_caught_up: 'আপনি সব দেখেছেন',
    whats_new_caught_up_desc: 'গত ৭ দিনে নতুন কিছু নেই। নতুন প্রকাশিত ফিচার এখানে স্বয়ংক্রিয়ভাবে দেখাবে।',
    whats_new_show_dismissed: 'বাতিল করা আইটেম দেখান',
    whats_new_mark_one: 'পড়া হিসাবে চিহ্নিত করুন',
    whats_new_mark_all: 'সব পড়া হিসাবে চিহ্নিত করুন',
    whats_new_today: 'আজ',
    whats_new_yesterday: 'গতকাল',
    whats_new_days_ago: '{n} দিন আগে',
    whats_new_n_items: '{n}টি আইটেম',
    whats_new_n_item: '১টি আইটেম',
    whats_new_try_feature: 'এই ফিচারটি চেষ্টা করুন',
    whats_new_got_it: 'বুঝেছি',
    whats_new_close: 'বন্ধ করুন',
    cat_ux: 'UX',
    cat_reliability: 'নির্ভরযোগ্যতা',
    cat_reconciliation: 'মিল মেলানো',
    cat_ai: 'AI',
  },
  fr: {
    new_badge_label: 'NOUVEAU',
    whats_new_title: 'Nouveautés',
    whats_new_subtitle: '7 derniers jours',
    whats_new_caught_up: 'Vous êtes à jour',
    whats_new_caught_up_desc: 'Rien de nouveau ces 7 derniers jours. Les fonctionnalités récentes apparaissent ici automatiquement.',
    whats_new_show_dismissed: 'Afficher les éléments masqués',
    whats_new_mark_one: 'Marquer comme lu',
    whats_new_mark_all: 'Tout marquer comme lu',
    whats_new_today: "aujourd'hui",
    whats_new_yesterday: 'hier',
    whats_new_days_ago: 'il y a {n} jours',
    whats_new_n_items: '{n} éléments',
    whats_new_n_item: '1 élément',
    whats_new_try_feature: 'Essayer cette fonctionnalité',
    whats_new_got_it: 'Compris',
    whats_new_close: 'Fermer',
    cat_ux: 'UX',
    cat_reliability: 'Fiabilité',
    cat_reconciliation: 'Rapprochement',
    cat_ai: 'IA',
  },
  es: {
    new_badge_label: 'NUEVO',
    whats_new_title: 'Novedades',
    whats_new_subtitle: 'Últimos 7 días',
    whats_new_caught_up: 'Estás al día',
    whats_new_caught_up_desc: 'Nada nuevo en los últimos 7 días. Las funciones recientes aparecen aquí automáticamente.',
    whats_new_show_dismissed: 'Mostrar elementos descartados',
    whats_new_mark_one: 'Marcar como leído',
    whats_new_mark_all: 'Marcar todo como leído',
    whats_new_today: 'hoy',
    whats_new_yesterday: 'ayer',
    whats_new_days_ago: 'hace {n} días',
    whats_new_n_items: '{n} elementos',
    whats_new_n_item: '1 elemento',
    whats_new_try_feature: 'Probar esta función',
    whats_new_got_it: 'Entendido',
    whats_new_close: 'Cerrar',
    cat_ux: 'UX',
    cat_reliability: 'Confiabilidad',
    cat_reconciliation: 'Conciliación',
    cat_ai: 'IA',
  },
  tr: {
    new_badge_label: 'YENİ',
    whats_new_title: 'Yenilikler',
    whats_new_subtitle: 'Son 7 gün',
    whats_new_caught_up: 'Hepsini gördünüz',
    whats_new_caught_up_desc: 'Son 7 günde yeni bir şey yok. Yeni eklenen özellikler burada otomatik görünür.',
    whats_new_show_dismissed: 'Yoksayılanları göster',
    whats_new_mark_one: 'Okundu işaretle',
    whats_new_mark_all: 'Tümünü okundu işaretle',
    whats_new_today: 'bugün',
    whats_new_yesterday: 'dün',
    whats_new_days_ago: '{n} gün önce',
    whats_new_n_items: '{n} öğe',
    whats_new_n_item: '1 öğe',
    whats_new_try_feature: 'Bu özelliği dene',
    whats_new_got_it: 'Anladım',
    whats_new_close: 'Kapat',
    cat_ux: 'UX',
    cat_reliability: 'Güvenilirlik',
    cat_reconciliation: 'Mutabakat',
    cat_ai: 'YZ',
  },
  tl: {
    new_badge_label: 'BAGO',
    whats_new_title: 'Mga Bago',
    whats_new_subtitle: 'Huling 7 araw',
    whats_new_caught_up: 'Bago lahat sa iyo',
    whats_new_caught_up_desc: 'Walang bago sa nakalipas na 7 araw. Awtomatikong lalabas dito ang mga bagong feature.',
    whats_new_show_dismissed: 'Ipakita ang mga itinapon',
    whats_new_mark_one: 'Markahan bilang basa na',
    whats_new_mark_all: 'Markahan lahat bilang basa na',
    whats_new_today: 'ngayong araw',
    whats_new_yesterday: 'kahapon',
    whats_new_days_ago: '{n} araw na ang nakalipas',
    whats_new_n_items: '{n} item',
    whats_new_n_item: '1 item',
    whats_new_try_feature: 'Subukan ang feature na ito',
    whats_new_got_it: 'Naintindihan',
    whats_new_close: 'Isara',
    cat_ux: 'UX',
    cat_reliability: 'Pagiging Maaasahan',
    cat_reconciliation: 'Pagkatugma',
    cat_ai: 'AI',
  },
  ml: {
    new_badge_label: 'പുതിയത്',
    whats_new_title: 'പുതുതായി എന്താണ്',
    whats_new_subtitle: 'കഴിഞ്ഞ 7 ദിവസം',
    whats_new_caught_up: 'നിങ്ങൾ പുതുക്കിയിട്ടുണ്ട്',
    whats_new_caught_up_desc: 'കഴിഞ്ഞ 7 ദിവസത്തിനുള്ളിൽ പുതിയത് ഒന്നുമില്ല. പുതുതായി പുറത്തിറക്കിയ സവിശേഷതകൾ ഇവിടെ സ്വയം ദൃശ്യമാകും.',
    whats_new_show_dismissed: 'ഒഴിവാക്കിയ ഇനങ്ങൾ കാണിക്കുക',
    whats_new_mark_one: 'വായിച്ചതായി അടയാളപ്പെടുത്തുക',
    whats_new_mark_all: 'എല്ലാം വായിച്ചതായി അടയാളപ്പെടുത്തുക',
    whats_new_today: 'ഇന്ന്',
    whats_new_yesterday: 'ഇന്നലെ',
    whats_new_days_ago: '{n} ദിവസം മുമ്പ്',
    whats_new_n_items: '{n} ഇനങ്ങൾ',
    whats_new_n_item: '1 ഇനം',
    whats_new_try_feature: 'ഈ ഫീച്ചർ പരീക്ഷിക്കൂ',
    whats_new_got_it: 'മനസ്സിലായി',
    whats_new_close: 'അടയ്ക്കുക',
    cat_ux: 'UX',
    cat_reliability: 'വിശ്വാസ്യത',
    cat_reconciliation: 'പൊരുത്തപ്പെടുത്തൽ',
    cat_ai: 'AI',
  },
};
